import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Tours', path: '/tours' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '#contact' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-lg backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="https://static.readdy.ai/image/9396f31d77c954f423ce345f87cd25c5/87c3864ad4b2a7213a20510bc18b6a66.png" 
              alt="Candy World Tours Logo" 
              className="h-12 w-auto transition-transform group-hover:scale-105"
            />
            <span className={`text-xl font-bold transition-colors ${
              isScrolled ? 'text-navy-900' : 'text-white'
            }`}>
              Candy World Tours
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-all relative group whitespace-nowrap ${
                  isScrolled ? 'text-navy-700 hover:text-accent-pink' : 'text-white hover:text-accent-pink'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-accent-pink transition-all group-hover:w-full ${
                  location.pathname === link.path ? 'w-full' : ''
                }`}></span>
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="https://wa.me/1234567890"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                isScrolled ? 'bg-white/10 hover:bg-accent-pink text-navy-700 hover:text-white' : 'bg-white/20 hover:bg-white text-white'
              }`}
            >
              <i className="ri-whatsapp-line text-xl"></i>
            </a>
            <Link
              to="/tours"
              className="px-6 py-2.5 bg-gradient-to-r from-primary-600 to-accent-pink text-white rounded-full font-semibold text-sm hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Book Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isScrolled ? 'text-navy-900' : 'text-white'
            }`}
          >
            <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-white shadow-xl"
        >
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-navy-700 hover:text-accent-pink font-medium transition-colors cursor-pointer"
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/tours"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full px-6 py-3 bg-gradient-to-r from-primary-600 to-accent-pink text-white rounded-full font-semibold text-center hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Book Now
            </Link>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
}